import { businessTypes } from "./businessCategories";
import { classifyBusinessCategoryApi } from "../Store/apiStore";

/**
 * classifyBusinessCategory — use ultra-fast local matching + secure backend proxy
 * to pick the SINGLE best-matching category out of our fixed list in
 * lib/businessCategories.js.
 *
 * @param {object} business  { name, categories:[googleTypes], website, address }
 * @returns {Promise<string|null>}  a category from businessTypes[].type
 */

const CATEGORIES = businessTypes.map((b) => b.type).filter(Boolean);

/* ── 1. Instant local keyword match (<1ms execution overhead) ── */
function quickLocalMatch(business) {
  const combined = (
    (business.name || "") + " " + 
    (business.website || "") + " " + 
    (Array.isArray(business.categories) ? business.categories.join(" ") : "")
  ).toLowerCase();

  if (!combined.trim()) return null;

  // const rules = [
  //   { keywords: ["plumb"], type: "Plumber" },
  //   { keywords: ["roof"], type: "Roofers" },
  //   { keywords: ["dentis", "dental"], type: "Dentist" },
  //   { keywords: ["electric"], type: "Electrician" },
  //   { keywords: ["hvac", "air condition", "heating and cooling", "furnace"], type: "HVAC Services" },
  //   { keywords: ["salon", "barber", "hair stylist"], type: "Salon" },
  //   { keywords: ["janitor", "maid", "deep clean"], type: "Cleaning and Janitorial Services" },
  //   { keywords: ["bakery", "cake shop", "pastry"], type: "Bakery" },
  //   { keywords: ["gym", "fitness", "yoga", "crossfit"], type: "Gym & Fitness Center" },
  //   { keywords: ["law firm", "attorney", "legal"], type: "Law Firm" },
  //   { keywords: ["accounting", "bookkeep", "tax prep"], type: "Accounting Services" },
  //   { keywords: ["restaurant", "bistro", "diner", "dhaba", "eatery", "food court"], type: "Restaurant" },
  //   { keywords: ["realtor", "real estate", "realty"], type: "Real Estate Broker" },
  //   { keywords: ["landscaping", "lawn care"], type: "Landscaping Company" },
  //   { keywords: ["flooring", "hardwood", "carpet"], type: "Flooring" },
  //   { keywords: ["dry clean", "laundry"], type: "Dry Cleaners" },
  //   { keywords: ["car repair", "auto repair", "garage"], type: "Car Repair & Garage" },
  //   { keywords: ["interior design"], type: "Interior Designer" },
  //   { keywords: ["web design", "website develop"], type: "Web Design Agency" },
  //   { keywords: ["e-commerce", "shopify", "online store"], type: "E-commerce" },
  //   { keywords: ["construct", "builder", "remodel", "renovat"], type: "Construction Services" },
  //   { keywords: ["general contractor"], type: "General Contractor" },
  //   { keywords: ["handyman", "home repair"], type: "Home Services" },
  //   { keywords: ["mover", "packer", "relocation"], type: "Movers and Packers" },
  //   { keywords: ["trucking", "freight", "logistics"], type: "Trucking Company" },
  //   { keywords: ["taxi", "cab", "limo"], type: "Taxi Service" },
  //   { keywords: ["insurance"], type: "Insurance Agency" },
  //   { keywords: ["architect"], type: "Architect" },
  // ];
const rules = [
  // Home / field services
  { keywords: ["plumb", "drain", "sewer", "water heater", "pipe repair", "leak repair", "rooter"], type: "Plumber" },

  { keywords: ["roof", "roofer", "roofing", "shingle", "gutter", "siding"], type: "Roofers" },

  { keywords: ["electric", "electrician", "electrical", "wiring", "breaker", "panel upgrade"], type: "Electrician" },

  { keywords: ["hvac", "air conditioning", "ac repair", "a/c repair", "heating and cooling", "heating & cooling", "furnace", "boiler", "duct cleaning"], type: "HVAC Services" },

  { keywords: ["landscaping", "landscape", "lawn care", "lawn service", "tree service", "yard maintenance", "snow removal"], type: "Landscaping Company" },

  { keywords: ["flooring", "hardwood", "carpet", "tile installation", "vinyl flooring", "laminate flooring", "epoxy floor"], type: "Flooring" },

  { keywords: ["general contractor", "gc contractor"], type: "General Contractor" },

  { keywords: ["construct", "construction", "builder", "remodel", "renovat", "home improvement", "kitchen remodel", "bath remodel", "basement finishing"], type: "Construction Services" },

  { keywords: ["handyman", "home repair", "property maintenance", "fix-it", "repair service"], type: "Home Services" },

  // Health / personal care
  { keywords: ["dentis", "dental", "orthodont", "endodont", "periodont", "oral surgery", "smile clinic"], type: "Dentist" },

  { keywords: ["salon", "barber", "hair stylist", "hair studio", "beauty salon", "nail salon", "lash studio", "brow studio"], type: "Salon" },

  { keywords: ["gym", "fitness", "yoga", "pilates", "crossfit", "personal training", "martial arts", "boxing club"], type: "Gym & Fitness Center" },

  // Cleaning / laundry
  { keywords: ["cleaning", "cleaners", "janitor", "janitorial", "maid", "housekeeping", "deep clean", "pressure washing", "carpet cleaning"], type: "Cleaning and Janitorial Services" },

  { keywords: ["dry clean", "dryclean", "laundry", "laundromat", "wash and fold", "alteration"], type: "Dry Cleaners" },

  // Food
  { keywords: ["bakery", "cake shop", "pastry", "patisserie", "cupcake", "dessert shop", "bakehouse"], type: "Bakery" },

  { keywords: ["restaurant", "bistro", "diner", "dhaba", "eatery", "food court", "cafe", "coffee shop", "pizzeria", "grill", "barbecue", "bbq", "catering"], type: "Restaurant" },

  // Professional services
  { keywords: ["law firm", "attorney", "lawyer", "legal", "solicitor", "immigration law", "family law", "personal injury"], type: "Law Firm" },

  { keywords: ["accounting", "accountant", "bookkeep", "bookkeeping", "tax prep", "tax service", "payroll", "cpa"], type: "Accounting Services" },

{ keywords: ["insurance", "insurance agency", "insurance broker", "life insurance", "auto insurance", "home insurance", "business insurance"], type: "Insurance Agency" },

 { keywords: ["architect", "architecture", "architectural", "architecture studio"], type: "Architect" },

  { keywords: ["interior design", "interiors", "home decor", "space planning", "interior studio"], type: "Interior Designer" },

  // Real estate
  { keywords: ["realtor", "real estate", "realty", "brokerage", "property management", "property manager", "letting agency"], type: "Real Estate Broker" },

  // Automotive / transport
  { keywords: ["auto repair", "car repair", "mechanic", "body shop", "collision", "tire shop", "oil change", "transmission repair", "auto garage"], type: "Car Repair & Garage" },

  { keywords: ["mover", "moving company", "packer", "relocation", "storage moving", "furniture moving"], type: "Movers and Packers" },

  { keywords: ["trucking", "freight", "logistics", "dispatch", "cargo", "hauling", "transportation"], type: "Trucking Company" },

  { keywords: ["taxi", "cab", "limo", "limousine", "chauffeur", "airport transfer", "car service"], type: "Taxi Service" },

  // Digital / online business
  { keywords: ["web design", "website design", "website develop", "web development", "digital agency", "seo agency", "wordpress", "app development"], type: "Web Design Agency" },

  { keywords: ["e-commerce", "ecommerce", "online store", "shopify", "woocommerce", "magento", "amazon seller", "etsy shop"], type: "E-commerce" },
];
  for (const rule of rules) {
    if (rule.keywords.some((k) => combined.includes(k))) {
      const matched = CATEGORIES.find((c) => c.toLowerCase() === rule.type.toLowerCase());
      if (matched) return matched;
    }
  }

  return null;
}

/* ── helper: match backend reply against our fixed category list ── */
function matchCategory(text) {
  if (!text) return null;

  const cleaned = text
    .replace(/^[\s"'`*\-•]+|[\s"'`*\-•]+$/g, "")
    .trim();

  if (!cleaned) return null;
  const lower = cleaned.toLowerCase();

  const exact = CATEGORIES.find((c) => c.toLowerCase() === lower);
  if (exact) return exact;

  const lines = cleaned.split(/\r?\n/).map((l) => l.replace(/^[\s"'`*\-•]+|[\s"'`*\-•]+$/g, "").trim()).filter(Boolean);
  for (const line of lines) {
    const lineLower = line.toLowerCase();
    const lineExact = CATEGORIES.find((c) => c.toLowerCase() === lineLower);
    if (lineExact) return lineExact;
  }

  const partial = CATEGORIES.find(
    (c) => lower.includes(c.toLowerCase()) || c.toLowerCase().includes(lower)
  );
  if (partial) return partial;

  for (const line of lines) {
    const lineLower = line.toLowerCase();
    const linePartial = CATEGORIES.find(
      (c) => lineLower.includes(c.toLowerCase()) || c.toLowerCase().includes(lineLower)
    );
    if (linePartial) return linePartial;
  }

  return null;
}

/* ── main export ── */

/*
 * Category ke saath ye bhi batata hai ki wo AAYI KAHAN SE.
 *
 * Ye function do bilkul alag raaste use karta hai — local keyword match aur
 * backend/Gemini call — par bahar dono ek jaisi dikhti thi (bas ek string).
 * Isliye analytics me har auto-pick "gemini" gina jata tha, chahe Gemini se
 * baat hui bhi na ho. Ab source alag se milta hai:
 *
 *   local_match      — quickLocalMatch ne turant pehchan liya, Gemini call nahi hui
 *   gemini           — backend ne category di aur wo hamari list se match ho gayi
 *   gemini_unmatched — backend ne jawab diya par hamari list me map nahi hua
 *   gemini_error     — backend call fail
 *   no_categories    — CATEGORIES list hi khaali hai
 *
 * Category na mile to `category` null hota hai, par `source` phir bhi batata
 * hai ki kis step par baat rukI.
 */
export async function classifyBusinessCategoryWithSource(business = {}) {
  // 1. Instant local fast match (<1ms)
  const quick = quickLocalMatch(business);
  if (quick) {
    // console.log("[classifyBusiness] Instant local match:", quick);
    return { category: quick, source: "local_match" };
  }

  if (!CATEGORIES.length) return { category: null, source: "no_categories" };

  try {
    // 2. Call backend proxy endpoint securely (no API keys exposed in browser network tab)
    const backendCategory = await classifyBusinessCategoryApi(business, CATEGORIES);
    const matched = matchCategory(backendCategory);
    return {
      category: matched,
      source: matched ? "gemini" : "gemini_unmatched",
    };
  } catch (e) {
    console.error("classifyBusinessCategory: Backend call failed:", e?.message);
    return { category: null, source: "gemini_error" };
  }
}

// Purana string-wala shape — jise sirf category chahiye wo isi ko use kare.
export async function classifyBusinessCategory(business = {}) {
  const { category } = await classifyBusinessCategoryWithSource(business);
  return category;
}

export default classifyBusinessCategory;
